import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

/** Mark a lead lost with a reason — used from the Leads page. */
export function LostLeadDialog({ lead, onClose, onSaved }) {
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({ reason: "", remarks: "", follow_up_after_days: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lead-roi/reasons").then(({ data }) => setReasons(data.reasons)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.reason) return toast.error("Pick why this lead was lost");
    setBusy(true);
    try {
      await api.post(`/lead-roi/leads/${lead.id}/lost`, {
        ...form, follow_up_after_days: Number(form.follow_up_after_days) || 0 });
      toast.success("Marked lost — history kept, you can reactivate later");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface fixed inset-x-4 top-24 z-50 mx-auto max-w-md p-4 shadow-lg sm:inset-x-auto"
      data-testid="lost-lead-dialog">
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Mark lost — {lead.title}</span>
      </div>
      <div className="mt-3 grid gap-3">
        <Field label="Reason">
          <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
            <SelectTrigger data-testid="lost-reason-select">
              <SelectValue placeholder="Why did we lose it?" />
            </SelectTrigger>
            <SelectContent>
              {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Remarks">
          <Input data-testid="lost-remarks-input" value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
        </Field>
        <Field label="Try again after (days)">
          <Input data-testid="lost-followup-input" value={form.follow_up_after_days}
            onChange={(e) => setForm({ ...form, follow_up_after_days: e.target.value })} />
        </Field>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} data-testid="lost-cancel">Cancel</Button>
        <Button disabled={busy} onClick={save} data-testid="lost-save">
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Mark lost
        </Button>
      </div>
    </div>
  );
}
